import { useMemo } from 'react'

import { IrisValueObjectsExportFormatEnum } from 'generated/resources/graphql'

import { getRenderedDefFromTableId } from 'modules/ai_reports/components/DatasourceTypeRenderer/tableRendererRegistry'
import { DateNavigationFilter } from 'modules/ai_reports/components/DateNavigationFilter'
import { Layout } from 'modules/ai_reports/components/Layout'
import { ReportWidget } from 'modules/ai_reports/components/ReportWidget'
import { SaveModal } from 'modules/ai_reports/components/SaveModal'
import { useColumnOrderTracking } from 'modules/ai_reports/hooks/useColumnOrderTracking'
import { useExportHeaderActions } from 'modules/ai_reports/hooks/useExportHeaderActions'
import { useReportSchema } from 'modules/ai_reports/hooks/useReportSchema'
import {
  getDateNavigatorConfigArg,
  getInitialDateFilter,
  useReportState,
} from 'modules/ai_reports/hooks/useReportState'
import { ReportType } from 'modules/ai_reports/lib/types'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

type ReportDetailProps = {
  report: ReportType
}

export const ReportDetail = ({ report }: ReportDetailProps) => {
  const [schema, handleSchemaChanged] = useReportSchema(
    report.datasource?.columnsConnection?.nodes ?? []
  )

  const { state, dispatch, agentState, NavConfirmModal, saveReport, handleExport } = useReportState(
    {
      datasourceId: report.datasource?.id || '',
      schema,
      initialReport: report,
      initialDateFilter: getInitialDateFilter(report.filters),
      initialFilters: report.filters,
      initialColumnFormatHints: report.formatHints.map((hint) => ({
        column_alias: hint.columnDisplayName,
        column_format: hint.formatHint,
      })),
      reportName: report.name,
    }
  )

  const { handleColumnStateChange, flushColumnOrder } = useColumnOrderTracking(
    state.columnOrder,
    dispatch
  )

  const rendererDef = getRenderedDefFromTableId(report.datasource?.id || '')
  const dateNavigatorConfig = rendererDef?.dateNavigatorConfig?.(
    getDateNavigatorConfigArg(report.filters)
  )
  const datasourceExportFormats = report.datasource?.exportFormats
  const exportFormats = useMemo<IrisValueObjectsExportFormatEnum[]>(
    () =>
      datasourceExportFormats && datasourceExportFormats.length > 0
        ? datasourceExportFormats
        : ['csv'],
    [datasourceExportFormats]
  )
  useExportHeaderActions({ exportFormats, loading: state.isExporting })

  return (
    <>
      <ResponsivePage.Body
        variant="fullscreen"
        onActionClick={(key) => {
          if (key === 'save') {
            flushColumnOrder()
            dispatch({ type: 'UNSAVED_CHANGES_CONFIRM_REQUESTED' })
            return
          }

          const exportFormat = exportFormats.find((format) => key === `export:${format}`)
          if (exportFormat) handleExport(exportFormat)
        }}
      >
        <Layout
          header={
            dateNavigatorConfig && (
              <DateNavigationFilter
                config={dateNavigatorConfig}
                onSelect={(value) => {
                  dispatch({ type: 'DATE_FILTER_UPDATED', payload: value?.value })
                }}
                value={
                  state.dateFilter
                    ? {
                        value: {
                          from: state.dateFilter.from,
                          to: state.dateFilter.to ?? state.dateFilter.from,
                        },
                        granularity: dateNavigatorConfig.granularity,
                      }
                    : undefined
                }
              />
            )
          }
          paddingX="s16"
        >
          {state.report &&
            state.report.widgets.map((widget, index) => {
              const currentReport = state.report
              if (!currentReport) return null

              return (
                <ReportWidget
                  id={currentReport.id}
                  key={index}
                  sqlQuery={widget.query}
                  description={widget.description}
                  dateFilter={state.dateFilter}
                  tableId={currentReport.datasource?.id || ''}
                  columnFormatHints={agentState?.sqlAnswer?.column_format_hints ?? []}
                  columnOrder={state.columnOrder}
                  reportName={currentReport.name}
                  currentFilters={state.datatableFilters ?? undefined}
                  onStateChange={(s) => {
                    if (s.filters) {
                      dispatch({ type: 'DATACOLLECTION_FILTERS_UPDATED', payload: s.filters })
                    }
                    handleColumnStateChange(s.settings)
                  }}
                  schema={schema}
                  onSchemaChanged={handleSchemaChanged}
                />
              )
            })}
          <NavConfirmModal />
          <SaveModal
            showSaveAsNewReport
            open={state.saveModalOpen}
            name={state.report?.name}
            description={state.report?.description}
            onClose={() => dispatch({ type: 'UNSAVED_CHANGES_DISMISSED' })}
            onClickSave={({ name, description, saveAsNew }) =>
              saveReport({ name, description, saveAsNew })
            }
          />
        </Layout>
      </ResponsivePage.Body>
    </>
  )
}
